import { deriveComposerPlanProgress } from "./deriveComposerPlanProgress";
import type { ComposerDisabledReason } from "./composerTurnPlan";
import { type ServerProvider, type TurnId } from "@cmmd-forge/contracts";
import type {
  ForgeProviderAccessState,
  ProviderCredentialMode,
} from "../../cmmd/forgeProviderAccess";
import { providerPolicyForAccess } from "../../cmmd/forgeProviderAccess";
import type { ModelPickerProviderAvailability } from "./modelPickerProviderAvailability";
import { type LoadedSurfaceInput } from "./buildChatLoadedThreadSurfaceProps";
import { type useChatRouteRuntime } from "./useChatRouteRuntime";
import { type useChatThreadActionsRuntime } from "./useChatThreadActionsRuntime";
import { type useChatThreadControlsRuntime } from "./useChatThreadControlsRuntime";
import { type useChatThreadRuntimeState } from "./useChatThreadRuntimeState";
import { composerPresentationSendBusy } from "./composerPresentationSendBusy";

export function buildChatComposerRuntimeInput(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly controlsRuntime: ReturnType<typeof useChatThreadControlsRuntime>;
  readonly actions: ReturnType<typeof useChatThreadActionsRuntime>;
  readonly onOpenUsageLimits: () => boolean;
  readonly providerState: {
    readonly selectedProvider: LoadedSurfaceInput["composerProps"]["resolvedProvider"];
    readonly lockedProvider: LoadedSurfaceInput["composerProps"]["lockedProvider"];
    readonly composerDisabledReason: ComposerDisabledReason;
    readonly providerAccess: ForgeProviderAccessState;
    readonly providerCredentialMode: ProviderCredentialMode;
    readonly providerAvailability: ReadonlyArray<ModelPickerProviderAvailability>;
  };
}) {
  return {
    composerProps: {
      runtimeAdmission: input.actions.runtimeAdmission,
      ...buildComposerStateProps(input),
      ...buildComposerHandlerProps(input),
    },
  };
}

function buildComposerStateProps(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly controlsRuntime: ReturnType<typeof useChatThreadControlsRuntime>;
  readonly providerState: {
    readonly selectedProvider: LoadedSurfaceInput["composerProps"]["resolvedProvider"];
    readonly lockedProvider: LoadedSurfaceInput["composerProps"]["lockedProvider"];
    readonly composerDisabledReason: ComposerDisabledReason;
    readonly providerAccess: ForgeProviderAccessState;
    readonly providerCredentialMode: ProviderCredentialMode;
    readonly providerAvailability: ReadonlyArray<ModelPickerProviderAvailability>;
  };
}) {
  const { route, bootstrap, composer, settings, theme } = input.routeRuntime;
  const { activity, pendingInput, dispatch } = input.threadRuntime;
  const { panels, shortcuts, workspace, scroll } = input.controlsRuntime;
  return {
    ref: composer.setComposerHandle,
    composerDraftTarget: bootstrap.composerDraftTarget,
    environmentId: route.environmentId,
    routeKind: route.routeKind,
    routeThreadRef: bootstrap.routeThreadRef,
    draftId: route.draftId,
    activeThreadId: bootstrap.activeThreadId,
    isServerThread: bootstrap.isServerThread,
    isLocalDraftThread: bootstrap.isLocalDraftThread,
    phase: activity.phase,
    isConnecting: buildComposerConnectingState(input),
    isSendBusy: composerPresentationSendBusy({
      localDispatchBusy: dispatch.isSendBusy,
      pendingAutoSubmit: bootstrap.composerPendingAutoSubmit,
    }),
    isPreparingWorktree: dispatch.isPreparingWorktree,
    activePendingApproval: activity.activePendingApproval,
    activeActionRequired: activity.activeActionRequired,
    pendingApprovals: activity.pendingApprovals,
    pendingUserInputs: activity.pendingUserInputs,
    activePendingProgress: pendingInput.activePendingProgress,
    activePendingResolvedAnswers: pendingInput.activePendingResolvedAnswers,
    activePendingIsResponding: pendingInput.activePendingIsResponding,
    activePendingDraftAnswers: pendingInput.activePendingDraftAnswers,
    activePendingQuestionIndex: pendingInput.activePendingQuestionIndex,
    pendingUserInputError: pendingInput.pendingUserInputError,
    respondingRequestIds: input.threadRuntime.providerResponses.respondingRequestIds,
    showPlanFollowUpPrompt: activity.showPlanFollowUpPrompt,
    activeProposedPlan: activity.activeProposedPlan,
    planProgress: deriveComposerPlanProgress(
      activity.activePlan,
      bootstrap.activeThread?.latestTurn?.turnId ?? null,
      input.threadRuntime.projectContext.latestTurnSettled,
    ),
    activePlan: activity.activePlan as { readonly turnId?: TurnId } | null,
    sidebarProposedPlan: activity.sidebarProposedPlan as {
      readonly turnId?: TurnId;
    } | null,
    planSidebarLabel: activity.planSidebarLabel,
    planSidebarOpen: panels.planOpen,
    runtimeMode: bootstrap.runtimeMode,
    interactionMode: bootstrap.interactionMode,
    resolvedProvider: input.providerState.selectedProvider,
    lockedProvider: input.providerState.lockedProvider,
    composerDisabledReason: input.providerState.composerDisabledReason,
    // CMMD Admin policy is the single offerability/wallet authority for both rendering and dispatch.
    orgProviderPolicy: providerPolicyForAccess(input.providerState.providerAccess),
    // The Provider Inventory as published, not the eligible subset: filtering
    // here is what made a Withheld Provider vanish from the picker instead of
    // carrying its reason. `providerAvailability` is what tells the picker
    // which of these a person may not select, and why.
    providerStatuses: [...(bootstrap.providerStatuses as ServerProvider[])],
    providerAvailability: input.providerState.providerAvailability,
    activeProjectDefaultModelSelection:
      input.threadRuntime.projectContext.activeProject?.defaultModelSelection,
    activeThreadModelSelection: bootstrap.activeThread?.modelSelection,
    keybindings: shortcuts.keybindings,
    resolvedTheme: theme.resolvedTheme,
    settings,
    gitCwd: workspace.gitCwd,
    promptRef: composer.promptRef,
    composerImagesRef: composer.composerImagesRef,
    composerTerminalContextsRef: composer.composerTerminalContextsRef,
    shouldAutoScrollRef: scroll.isAtEndRef,
    scheduleStickToBottom: scroll.scrollToEnd,
    getTimelineScrollNode: scroll.getTimelineScrollNode,
    isTimelineAtEnd: scroll.isTimelineAtEnd,
  };
}

function buildComposerHandlerProps(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly controlsRuntime: ReturnType<typeof useChatThreadControlsRuntime>;
  readonly actions: ReturnType<typeof useChatThreadActionsRuntime>;
  readonly onOpenUsageLimits: () => boolean;
}) {
  const { composer, bootstrap } = input.routeRuntime;
  const { pendingInput, providerResponses } = input.threadRuntime;
  const { composerModes, panels } = input.controlsRuntime;
  return {
    onSend: input.actions.onSend,
    onOpenUsageLimits: input.onOpenUsageLimits,
    onInterrupt: input.actions.onInterrupt,
    onImplementPlanInNewThread: input.actions.onImplementPlanInNewThread,
    onRespondToApproval: providerResponses.onRespondToApproval,
    onRetryGitHubActionRequired: input.actions.onRetryGitHubActionRequired,
    onSelectActivePendingUserInputOption: pendingInput.onSelectActivePendingUserInputOption,
    onAdvanceActivePendingUserInput: pendingInput.onAdvanceActivePendingUserInput,
    onQuestionFilesChange: pendingInput.onQuestionFilesChange,
    onPreviousActivePendingUserInputQuestion: pendingInput.onPreviousActivePendingUserInputQuestion,
    onChangeActivePendingUserInputCustomAnswer:
      pendingInput.onChangeActivePendingUserInputCustomAnswer,
    onProviderModelSelect: input.actions.onProviderModelSelect,
    toggleInteractionMode: composerModes.toggleInteractionMode,
    handleRuntimeModeChange: composerModes.handleRuntimeModeChange,
    handleInteractionModeChange: composerModes.handleInteractionModeChange,
    togglePlanSidebar: panels.togglePlanSidebar,
    focusComposer: composer.focusComposer,
    scheduleComposerFocus: composer.scheduleComposerFocus,
    setThreadError: bootstrap.setThreadError,
    onExpandImage: input.routeRuntime.imagePreview.onExpandTimelineImage,
  };
}

function buildComposerConnectingState(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
}) {
  const { bootstrap, runtimeFlags } = input.routeRuntime;
  return (
    runtimeFlags.isConnecting ||
    ((bootstrap.serverThreadStatus === "loading" || bootstrap.serverThreadStatus === "hydrating") &&
      bootstrap.localDraftThread === undefined)
  );
}
